import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PackageOpen, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORTS = [
  { v: "newest", l: "Newest First" },
  { v: "price_low", l: "Price -- Low to High" },
  { v: "price_high", l: "Price -- High to Low" },
  { v: "name", l: "Name A–Z" },
];

export default function ProductList() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = params.get("search") || "";
  const category = params.get("category") || "";
  const featured = params.get("featured") || "";
  const sort = params.get("sort") || "newest";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("search", search);
      if (category) qs.set("category", category);
      if (featured) qs.set("featured", "true");
      qs.set("sort", sort);
      qs.set("page", page);
      qs.set("limit", "12");
      const { data } = await api.get(`/products?${qs.toString()}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, category, featured, sort, page]);

  useEffect(() => { load(); }, [load]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const catName = categories.find((c) => c.slug === category)?.name;
  const title = search ? `Results for "${search}"` : featured ? "Featured Deals" : category ? (catName || "Products") : "All Products";

  return (
    <div className="shell py-5 sm:py-7 animate-fade-up">
      {/* Breadcrumb */}
      <nav className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground pb-4">
        <Link to="/" className="hover:text-primary">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/products" className="hover:text-primary">Products</Link>
        {catName && (<><ChevronRight className="h-3 w-3" /><span className="text-foreground">{catName}</span></>)}
      </nav>

      <div className="flex gap-5 items-start">
        {/* Filter sidebar */}
        <aside className="hidden lg:block w-[260px] shrink-0 surface overflow-hidden sticky top-[130px]" data-testid="filter-sidebar">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-base font-extrabold text-deep">Filters</h2>
          </div>
          <div className="px-4 py-3 border-b border-border" data-testid="filter-category">
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Categories</p>
            <ul className="space-y-1">
              <li>
                <button onClick={() => setParam("category", "")} className={`text-sm w-full text-left py-1 hover:text-primary ${!category ? "text-primary font-medium" : "text-muted-foreground"}`}>
                  All Categories
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.slug}>
                  <button
                    onClick={() => setParam("category", c.slug)}
                    className={`text-sm w-full text-left py-1 flex justify-between hover:text-primary ${category === c.slug ? "text-primary font-medium" : "text-muted-foreground"}`}
                    data-testid={`filter-cat-${c.slug}`}
                  >
                    <span>{c.name}</span>
                    {c.product_count != null && <span className="text-xs">{c.product_count}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {(search || category || featured) && (
            <div className="px-4 py-3">
              <button onClick={() => setParams({})} className="text-sm text-primary font-medium uppercase" data-testid="filter-clear">Clear all</button>
            </div>
          )}
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0 surface overflow-hidden">
          <div className="px-4 sm:px-7 pt-5 sm:pt-6">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-deep" data-testid="product-list-title">{title}</h1>
            <p className="text-sm text-muted-foreground mt-1">Showing {data.items.length} of {data.total} products</p>
          </div>

          {/* Sort: tabs on desktop, selects on mobile */}
          <div className="hidden sm:flex items-center gap-6 px-7 mt-4 border-b border-border text-sm" data-testid="filter-sort">
            <span className="font-bold text-deep py-3">Sort by</span>
            {SORTS.map((s) => (
              <button
                key={s.v}
                onClick={() => setParam("sort", s.v === "newest" ? "" : s.v)}
                className={`py-3 border-b-2 -mb-px transition-colors ${sort === s.v ? "border-primary text-primary font-bold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                data-testid={`sort-${s.v}`}
              >
                {s.l}
              </button>
            ))}
          </div>
          <div className="sm:hidden flex gap-2 px-4 mt-4 pb-4 border-b border-border">
            <Select value={category || "all"} onValueChange={(v) => setParam("category", v === "all" ? "" : v)}>
              <SelectTrigger className="flex-1 h-9" data-testid="filter-category-mobile"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="flex-1 h-9" data-testid="filter-sort-mobile"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading products…</div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-20" data-testid="product-list-empty">
              <PackageOpen className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium text-lg">No products found</p>
              <p className="text-muted-foreground text-sm mt-1">Try adjusting your search or filters.</p>
              <Button className="mt-4" variant="outline" onClick={() => setParams({})}>Clear filters</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 p-4 sm:p-7">
                {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {data.pages > 1 && (
                <div className="flex items-center justify-center gap-3 py-6 border-t border-border">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam("page", page - 1)} data-testid="pagination-prev">Previous</Button>
                  <span className="text-sm text-muted-foreground px-2">Page {page} of {data.pages}</span>
                  <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setParam("page", page + 1)} data-testid="pagination-next">Next</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
